import { useEffect, useState, useRef } from 'react';

/**
 * Top-of-page indicator when the browser reports it's offline.
 * Firestore SDK already queues writes in IndexedDB while offline, so
 * nothing breaks — but the user has no feedback that we know. This
 * banner is that feedback.
 *
 * Auto-hides when we come back online (with a brief "✓ Conectado de
 * vuelta" success state).
 */
export default function OfflineBanner() {
  const [online, setOnline] = useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [justRecovered, setJustRecovered] = useState(false);

  const recoveryTimerRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setJustRecovered(true);
      // Show the "back online" message briefly, then hide.
      // Clear any pending timer first so quick toggles don't stack up.
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = setTimeout(() => setJustRecovered(false), 3000);
    };
    const handleOffline = () => {
      setOnline(false);
      setJustRecovered(false);
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    };
  }, []);

  if (online && !justRecovered) return null;

  if (justRecovered) {
    return (
      <div className="offline-banner offline-banner-recovered" role="status">
        <span>✓</span>
        <span>Conectado de vuelta — sincronizando tus cambios.</span>
      </div>
    );
  }

  return (
    <div className="offline-banner" role="alert">
      <span>📡</span>
      <strong>Sin conexión.</strong>
      <span>Tus cambios se guardan localmente y se sincronizarán cuando vuelva el internet.</span>
    </div>
  );
}
